import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const Admin = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [newQuestion, setNewQuestion] = useState({ text: '', options: ['', ''] });
  const [qrCodeSessionId, setQrCodeSessionId] = useState(null); // Track which session's QR is showing
  const [qrCode, setQrCode] = useState(null);
  const [results, setResults] = useState(null);

  // Load sessions on component mount
  useEffect(() => {
    loadSessions();
  }, []);

  // Poll for results if a session is selected
  useEffect(() => {
    let interval;
    if (selectedSession) {
      loadResults();
      interval = setInterval(loadResults, 3000); // Poll every 3 seconds
    }
    return () => clearInterval(interval);
  }, [selectedSession]);

  const loadSessions = async () => {
    try {
      const response = await axios.get('/api/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadResults = async () => {
    if (!selectedSession) return;
    try {
      const response = await axios.get(`/api/sessions/${selectedSession.id}/results`);
      setResults(response.data);
    } catch (error) {
      console.error('Error loading results:', error);
    }
  };

  const createSession = async () => {
    if (!newSessionTitle.trim()) return;

    try {
      const response = await axios.post('/api/sessions', { title: newSessionTitle });
      setSessions([response.data, ...sessions]);
      setNewSessionTitle('');
    } catch (error) {
      console.error('Error creating session:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Error creating session');
      }
    }
  };

  const addQuestion = async () => {
    if (!selectedSession || !newQuestion.text.trim() || newQuestion.options.some(opt => !opt.trim())) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`/api/sessions/${selectedSession.id}/questions`, {
        questionText: newQuestion.text,
        options: newQuestion.options
      });
      setNewQuestion({ text: '', options: ['', ''] });
      loadResults(); // Refresh results
    } catch (error) {
      console.error('Error adding question:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Error adding question');
      }
    }
  };

  const closeVoting = async (sessionId) => {
    if (!window.confirm('Are you sure you want to close voting for this session? No one will be able to vote anymore.')) {
      return;
    }

    try {
      await axios.post(`/api/sessions/${sessionId}/close`);
      // Update the session in the list
      setSessions(sessions.map(s =>
        s.id === sessionId ? { ...s, is_active: false } : s
      ));
    } catch (error) {
      console.error('Error closing voting:', error);
      alert('Error closing voting');
    }
  };

  const reopenVoting = async (sessionId) => {
    if (!window.confirm('Are you sure you want to re-open voting for this session? People will be able to vote again.')) {
      return;
    }

    try {
      await axios.post(`/api/sessions/${sessionId}/reopen`);
      // Update the session in the list
      setSessions(sessions.map(s =>
        s.id === sessionId ? { ...s, is_active: true } : s
      ));
    } catch (error) {
      console.error('Error reopening voting:', error);
      alert('Error reopening voting');
    }
  };

  const resetVotes = async (sessionId) => {
    if (!window.confirm('Are you sure you want to reset all votes for this session? This action cannot be undone!')) {
      return;
    }

    try {
      const response = await axios.post(`/api/sessions/${sessionId}/reset-votes`);
      alert(`Successfully reset ${response.data.deletedCount} vote(s)`);
      loadResults(); // Refresh results
    } catch (error) {
      console.error('Error resetting votes:', error);
      alert('Error resetting votes');
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session? This will delete all questions and votes.')) {
      return;
    }

    try {
      await axios.delete(`/api/sessions/${sessionId}`);
      setSessions(sessions.filter(s => s.id !== sessionId));
      if (selectedSession && selectedSession.id === sessionId) {
        setSelectedSession(null);
        setResults(null);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const generateQRCode = async (sessionId) => {
    // If clicking the same session, toggle it off
    if (qrCodeSessionId === sessionId) {
      setQrCodeSessionId(null);
      setQrCode(null);
      return;
    }

    try {
      const response = await axios.get(`/api/sessions/${sessionId}/qrcode`);
      setQrCode(response.data);
      setQrCodeSessionId(sessionId);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleSelectSession = (session) => {
    setSelectedSession(session);
    // Auto-scroll to questions section after it appears
    setTimeout(() => {
      const cards = document.querySelectorAll('.card');
      const questionsSection = Array.from(cards).find(card =>
        card.querySelector('h3') && card.querySelector('h3').textContent.includes('Add Question to:')
      );
      if (questionsSection) {
        questionsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const addOption = () => {
    setNewQuestion(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const updateOption = (index, value) => {
    setNewQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  return (
    <div>
      <h2 style={{ color: 'var(--white)', marginBottom: '2rem', textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)' }}>Admin Panel</h2>

      {/* Create New Session */}
      <div className="card">
        <h3>Create New Session</h3>
        <div className="form-group">
          <label>Session Title:</label>
          <input
            type="text"
            value={newSessionTitle}
            onChange={(e) => setNewSessionTitle(e.target.value)}
            placeholder="Enter session title"
          />
        </div>
        <button className="btn" onClick={createSession}>
          Create Session
        </button>
      </div>

      {/* Session List */}
      <div className="card">
        <h3>Your Sessions</h3>
        {sessions.length === 0 ? (
          <p>No sessions yet. Create one above!</p>
        ) : (
          <div>
            {sessions.map(session => (
              <div key={session.id} className="session-item">
                <h4>
                  {session.title}
                  {!session.is_active && (
                    <span style={{ marginLeft: '10px', color: '#dc3545', fontSize: '14px' }}>
                      (Voting Closed)
                    </span>
                  )}
                </h4>
                <p><small>ID: {session.id}</small></p>
                <div className="session-buttons">
                  <button
                    className="btn"
                    onClick={() => handleSelectSession(session)}
                  >
                    {selectedSession?.id === session.id ? 'Editing Questions' : 'Add Questions'}
                  </button>
                  <Link
                    to={`/results/${session.id}`}
                    className="btn"
                    style={{ backgroundColor: '#28a745' }}
                    target="_blank"
                  >
                    View Results
                  </Link>
                  <button
                    className="btn btn-secondary"
                    onClick={() => generateQRCode(session.id)}
                  >
                    {qrCodeSessionId === session.id ? 'Hide QR Code' : 'Show QR Code'}
                  </button>
                  {session.is_active ? (
                    <button
                      className="btn"
                      style={{ backgroundColor: '#ff9800' }}
                      onClick={() => closeVoting(session.id)}
                    >
                      Close Voting
                    </button>
                  ) : (
                    <button
                      className="btn"
                      style={{ backgroundColor: '#4caf50' }}
                      onClick={() => reopenVoting(session.id)}
                    >
                      Re-open Voting
                    </button>
                  )}
                  <button
                    className="btn"
                    style={{ backgroundColor: '#ff5722' }}
                    onClick={() => resetVotes(session.id)}
                  >
                    Reset Votes
                  </button>
                  <button
                    className="btn"
                    style={{ backgroundColor: '#dc3545' }}
                    onClick={() => deleteSession(session.id)}
                  >
                    Delete
                  </button>
                </div>

                {/* QR Code inline with session */}
                {qrCodeSessionId === session.id && qrCode && (
                  <div style={{
                    marginTop: '20px',
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <h4 style={{ marginBottom: '15px' }}>QR Code: {session.title}</h4>
                    <div className="qr-code">
                      <img src={qrCode.qrCode} alt="QR Code" style={{ maxWidth: '300px' }} />
                      <p style={{ marginTop: '10px' }}>
                        Voting URL: <a href={qrCode.voteUrl} target="_blank" rel="noopener noreferrer">{qrCode.voteUrl}</a>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Questions */}
      {selectedSession && (
        <div className="card">
          <h3>Add Question to: {selectedSession.title}</h3>
          {results && results.questions && results.questions.length > 0 ? (
            <div>
              <p style={{ color: '#666', fontStyle: 'italic' }}>
                This session already has a question. Only one question per session is allowed.
              </p>
              <p style={{ marginTop: '10px' }}>
                <strong>Current question:</strong> {results.questions[0].questionText}
              </p>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label>Question:</label>
                <input
                  type="text"
                  value={newQuestion.text}
                  onChange={(e) => setNewQuestion(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="Enter your question"
                />
              </div>

              <div className="form-group">
                <label>Options:</label>
                {newQuestion.options.map((option, index) => (
                  <div key={index} className="option-input">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                    />
                  </div>
                ))}
                <button className="btn btn-secondary" onClick={addOption}>
                  Add Option
                </button>
              </div>

              <button className="btn" onClick={addQuestion}>
                Add Question
              </button>
            </>
          )}
        </div>
      )}

      {/* Live Results */}
      {selectedSession && results && (
        <div className="card">
          <h3>Live Results: {selectedSession.title}</h3>
          <div className="results-container">
            {results.questions.map((question, index) => (
              <div key={question.questionId} className="question-results">
                <h3>{index + 1}. {question.questionText}</h3>
                <p><strong>Total Votes: {question.totalVotes}</strong></p>
                {question.results.map((result, idx) => (
                  <div key={idx} className="result-item">
                    <div className="result-info">
                      <span>{result.option}</span>
                      <span>{result.votes} votes ({result.percentage}%)</span>
                    </div>
                    <div className="result-bar">
                      <div
                        className="result-fill"
                        style={{ width: `${result.percentage}%` }}
                      >
                        {result.percentage}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;